import { useContext, useEffect, useState } from 'react';
import { Option, handleMultiSelectChange } from 'util/multi-select';
import { DisplayInformationFieldContext } from './display-information-field.context';
import {
  getInitialLanguageCode,
  languageOptions,
} from './display-information-field.utils';

const useDisplayLanguageSection = () => {
  const { field, fieldState, displayFieldSetDefaultValue } = useContext(
    DisplayInformationFieldContext,
  );

  const [activeTab, setActiveTab] = useState(
    getInitialLanguageCode(navigator.language.split('-')[0].toUpperCase()),
  );

  const handleLanguageDropdown = (value: Option[]) => {
    const selectedLanguages = handleMultiSelectChange(
      value,
      languageOptions(),
    )?.map(opt => opt.id);

    const updatedDisplayInformation = selectedLanguages?.map(languageCode => {
      const existingField = field.value?.find(
        info => info.languageCode === languageCode,
      );

      if (existingField) {
        return existingField;
      } else {
        // If language is added, create a new object with the language code
        return {
          languageCode,
          ...displayFieldSetDefaultValue,
        };
      }
    });

    field.onChange(updatedDisplayInformation);
  };

  const handleTabDragEnd = result => {
    const newTabs = Array.from(field.value);
    const draggedTab = newTabs.splice(result.source.index, 1)[0];
    newTabs.splice(result.destination.index, 0, draggedTab);

    field.onChange(newTabs);
  };

  const handleTabChange = (_e, selectedTab) => {
    setActiveTab(selectedTab);
  };

  const handleTabPanelChange = (index: number) => {
    setActiveTab(field.value[index]?.languageCode);
  };

  useEffect(() => {
    // Reset active Tab
    if (!field.value?.some(item => item.languageCode === activeTab))
      setActiveTab(field.value[0]?.languageCode);
  }, [activeTab, field.value]);

  useEffect(() => {
    if (!fieldState.error || !Array.isArray(fieldState.error)) return;

    const errorIndex = fieldState.error.findIndex(Boolean);

    const languageCode = (field.value as { languageCode: string }[])[errorIndex]
      ?.languageCode;
    setActiveTab(languageCode);
  }, [field.value, fieldState.error]);

  return {
    displayInformation: field.value,
    handleDisplayInformation: field.onChange,
    fieldState,
    handleLanguageDropdown,
    handleTabDragEnd,
    handleTabPanelChange,
    activeTab,
    handleTabChange,
  };
};

export default useDisplayLanguageSection;
